// VOY-485 (rebuild) — Connected accounts (Settings tab) — FULL-WIDTH.
//
// Status-only readout for the user's connected identity providers + payment
// method + calendar. Connect/disconnect lives elsewhere (auth provider
// settings + Stripe customer portal).
//
// SENSITIVE DATA RULE: payment method shown MASKED. We only render the
// brand + last4 that Stripe exposes — never a full PAN. Today no
// helper surfaces brand/last4 from the Stripe customer payment method,
// so we render "Not added" rather than fake a connection.

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

export interface ConnectedAccountsSectionProps {
  userId: string
}

interface ConnectionsState {
  google?: { email?: string | null } | null
  apple?: { email?: string | null } | null
  stripe?: { masked?: string | null } | null
  calendar?: { provider?: string | null } | null
}

export function ConnectedAccountsSection({ userId }: ConnectedAccountsSectionProps) {
  const [state, setState] = useState<ConnectionsState>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const { data } = await supabase.auth.getUser()
        const meta = (data?.user?.app_metadata ?? {}) as { providers?: string[]; provider?: string }
        const providers: string[] = Array.isArray(meta.providers)
          ? meta.providers
          : (meta.provider ? [meta.provider] : [])
        const email = data?.user?.email ?? null
        const next: ConnectionsState = {
          google: providers.includes('google') ? { email } : null,
          apple: providers.includes('apple') ? { email } : null,
          // Stripe + calendar surfaces not yet helper-wired — keep null
          // rather than fake a connection.
          stripe: null,
          calendar: null,
        }
        if (!cancelled) setState(next)
      } catch (err) {
        console.error('[ConnectedAccountsSection] load failed:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [userId])

  function row(name: string, sub: string | null, pillText: string, pillClass: 'pill mint' | 'pill') {
    return (
      <div className="connected-row" key={name}>
        <div>
          <div className="connected-name">{name}</div>
          <div className="connected-sub">{sub ?? '—'}</div>
        </div>
        <span className={pillClass}>{pillText}</span>
      </div>
    )
  }

  return (
    <section className="card" aria-labelledby="connected-h">
      <div className="card-h">
        <h3 id="connected-h">Connected accounts</h3>
        {loading && <span className="meta">LOADING…</span>}
      </div>
      <div className="stack sm">
        {row(
          'Google',
          state.google?.email ?? null,
          state.google ? 'Connected' : 'Not connected',
          state.google ? 'pill mint' : 'pill',
        )}
        {row(
          'Apple',
          state.apple?.email ?? null,
          state.apple ? 'Connected' : 'Not connected',
          state.apple ? 'pill mint' : 'pill',
        )}
        {row(
          'Stripe',
          'Payment method',
          state.stripe?.masked ?? 'Not added',
          state.stripe?.masked ? 'pill mint' : 'pill',
        )}
        {row(
          'Calendar',
          state.calendar?.provider ?? null,
          state.calendar ? 'Synced' : 'Not connected',
          state.calendar ? 'pill mint' : 'pill',
        )}
      </div>
    </section>
  )
}
